import type { ReactNode } from 'react';
import { cx } from '@/lib/cx';

/**
 * The consistent top of every page: what this is, then what you can do to it.
 *
 * `eyebrow`: owner decision 2026-10-11 removed the small-capitals TEXT line
 * above page titles ("OPERATIONS", "RECORDS"…). ⚠️ A plain-string eyebrow is
 * therefore rendered by nothing. But eight record pages pass a BACK LINK in
 * this slot (`<BackToQuotes />` and friends) — navigation, not a label — and
 * dropping it removed the only way back to the list on those pages. So a
 * non-string eyebrow still renders, in sentence case with no tracking. The
 * test is `typeof === 'string'`, which is exactly the label/link split today;
 * re-check it if a call site ever passes a label wrapped in an element.
 *
 * The actions wrap rather than holding their full width below `lg`: a record
 * page's header can carry ~835px of buttons, and a `shrink-0` row clipped
 * them off a 768px viewport. From `lg` up the row keeps its intrinsic width,
 * so the 1440 layout is unchanged.
 */
export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
  className,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cx(
        'flex flex-col gap-3 pb-5 sm:flex-row sm:items-start sm:justify-between sm:gap-6',
        className,
      )}
    >
      <div className="min-w-0">
        {eyebrow != null && typeof eyebrow !== 'string' && typeof eyebrow !== 'boolean' && (
          <div className="mb-1 text-xs text-fg-muted">{eyebrow}</div>
        )}
        {/* Wraps below `lg` so a long record title is readable on a phone;
            truncates from `lg` up exactly as before. */}
        <h1 className="m-0 break-words text-2xl font-semibold tracking-[-0.01em] text-fg lg:truncate">{title}</h1>
        {/*
         * A div, not a <p>: record pages need a supporting line carrying a
         * router Link and a Badge alongside the text, and interactive elements
         * inside a paragraph fight both the layout and the semantics.
         */}
        {description && (
          <div className="mt-1 flex max-w-2xl flex-wrap items-center gap-x-2 gap-y-1 text-sm text-fg-muted">
            {description}
          </div>
        )}
      </div>
      {actions && <div className="flex min-w-0 flex-wrap items-center gap-2 lg:shrink-0">{actions}</div>}
    </div>
  );
}
